import React, { useState, useEffect } from "react";
import useApi from "../../../CustomHooks/useApi";

/* ── TABLE STYLES ── */
const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
const TD = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle";

/* ══════════════════════════════════════════════ */
const Announcement = () => {

    const { callApi: postMessageApi }  = useApi("/admin/ticker/message", "POST");
    const { callApi: getHistoryApi }   = useApi("/admin/ticker/history", "GET");
    const { callApi: reactivateApi }   = useApi("/admin/ticker/reactivate", "POST");
    const { callApi: deactivateApi }   = useApi("/admin/ticker/deactivate", "POST");

    const [history, setHistory]               = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [newMessage, setNewMessage]         = useState("");
    const [postLoading, setPostLoading]       = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [reactivatingId, setReactivatingId] = useState(null);
    const [toast, setToast]                   = useState(null);

    /* ── toast ── */
    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── fetch history ── */
    const fetchHistory = async () => {
        setHistoryLoading(true);
        try {
            const res = await getHistoryApi(null, null);
            setHistory(Array.isArray(res) ? res : []);
        } catch (err) {
            showToast("error", "Failed to load message history");
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => { fetchHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── post new message ── */
    const handlePost = async () => {
        if (!newMessage.trim()) return;
        setPostLoading(true);
        try {
            await postMessageApi({ message: newMessage.trim() });
            showToast("success", "Announcement posted successfully");
            setNewMessage("");
            fetchHistory();
        } catch (err) {
            showToast("error", err?.response?.data?.message || "Failed to post message");
        } finally {
            setPostLoading(false);
        }
    };

    /* ── reactivate old message ── */
    const handleReactivate = async (id) => {
        setReactivatingId(id);
        try {
            await reactivateApi(null, `?id=${id}`);
            showToast("success", "Message reactivated successfully");
            fetchHistory();
        } catch (err) {
            showToast("error", err?.response?.data?.message || "Reactivation failed");
        } finally {
            setReactivatingId(null);
        }
    };

    /* ── clear ticker ── */
    const handleDeactivate = async () => {
        setDeactivateLoading(true);
        try {
            await deactivateApi(null, null);
            showToast("success", "Ticker cleared successfully");
            fetchHistory();
        } catch (err) {
            showToast("error", err?.response?.data?.message || "Failed to clear ticker");
        } finally {
            setDeactivateLoading(false);
        }
    };

    const activeMessage = history.find((m) => m.active);

    /* ══════════════ RENDER ══════════════ */
    return (
        <div className="w-full mx-auto pb-24">

            {/* TOAST */}
            {toast && (
                <div className={`fixed top-5 right-5 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all
                    ${toast.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                    <span>{toast.type === "success" ? "✓" : "✕"}</span>
                    <span>{toast.message}</span>
                    <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none">✕</button>
                </div>
            )}

            {/* HEADER */}
            <div className="mb-6 bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] rounded-2xl p-6 text-white shadow-lg">
                <h2 className="text-2xl font-bold">📢 Announcement</h2>
                <p className="text-blue-200 text-sm mt-1">Post and manage scrolling ticker messages on the merchant portal</p>
            </div>

            {/* ACTIVE TICKER BANNER */}
            {activeMessage && (
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                        <span className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse mt-1" />
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">Currently Active Ticker</p>
                            <p className="text-sm text-amber-900 font-medium break-words">{activeMessage.message}</p>
                            <p className="text-xs text-amber-600 mt-1">
                                Posted by <span className="font-semibold">{activeMessage.postedBy ?? "—"}</span>
                                {activeMessage.createdAt && <> · {activeMessage.createdAt}</>}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleDeactivate}
                        disabled={deactivateLoading}
                        className="flex-shrink-0 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors"
                    >
                        {deactivateLoading ? "Clearing..." : "✕ Clear Ticker"}
                    </button>
                </div>
            )}

            {/* POST NEW MESSAGE */}
            <div className="bg-white rounded-2xl shadow border overflow-hidden mb-5">
                <div className="px-5 py-4 border-b">
                    <h3 className="text-base font-bold text-gray-800">Post New Announcement</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {activeMessage
                            ? "This will replace the currently active ticker message"
                            : "No ticker is currently active — post one below"}
                    </p>
                </div>
                <div className="px-5 py-5">
                    <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="e.g. Scheduled maintenance tonight 11 PM – 12 AM, payouts may be delayed."
                        rows={3}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 resize-none mb-3"
                    />
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            onClick={handlePost}
                            disabled={!newMessage.trim() || postLoading}
                            className="flex items-center gap-2 bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors"
                        >
                            {postLoading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Posting...
                                </>
                            ) : "📢 Post Message"}
                        </button>
                        {!activeMessage && (
                            <button
                                onClick={handleDeactivate}
                                disabled={deactivateLoading}
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
                            >
                                {deactivateLoading ? "Clearing..." : "✕ Clear Ticker"}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* HISTORY TABLE */}
            <div className="bg-white rounded-2xl shadow border overflow-hidden">

                {/* Card header */}
                <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-800">Message History</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            {history.length} message{history.length !== 1 ? "s" : ""} — all messages ever posted, newest first
                        </p>
                    </div>
                    <button
                        onClick={fetchHistory}
                        disabled={historyLoading}
                        className="flex items-center gap-2 bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                    >
                        {historyLoading ? "Loading..." : "↻ Refresh"}
                    </button>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    {historyLoading ? (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-10 h-10 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin" />
                            <p className="text-gray-400 text-sm">Loading message history...</p>
                        </div>
                    ) : (
                        <table className="border-collapse text-sm" style={{ minWidth: 700, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th className={TH}>#</th>
                                    <th className={TH}>Message</th>
                                    <th className={TH}>Status</th>
                                    <th className={TH}>Posted By</th>
                                    <th className={TH}>Created At</th>
                                    <th className={TH}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="border border-[#94A3B8] py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                                                <p className="text-gray-500 text-sm font-medium">No announcements posted yet</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    history.map((msg, idx) => {
                                        const isReactivating = reactivatingId === msg.id;
                                        return (
                                            <tr key={msg.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                                <td className={TD}>{idx + 1}</td>
                                                <td className={`${TD} whitespace-normal max-w-[400px]`}>
                                                    <p className="text-gray-800 leading-relaxed">{msg.message}</p>
                                                </td>
                                                <td className={TD}>
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-semibold border
                                                        ${msg.active
                                                            ? "bg-green-100 text-green-700 border-green-200"
                                                            : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                                                        {msg.active && (
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                                                        )}
                                                        {msg.active ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td className={TD}>{msg.postedBy ?? "—"}</td>
                                                <td className={`${TD} whitespace-nowrap`}>{msg.createdAt ?? "—"}</td>
                                                <td className={TD}>
                                                    {msg.active ? (
                                                        <span className="text-xs text-gray-400 italic">Currently active</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleReactivate(msg.id)}
                                                            disabled={isReactivating}
                                                            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                                        >
                                                            {isReactivating ? (
                                                                <>
                                                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                    Reactivating...
                                                                </>
                                                            ) : "↺ Reactivate"}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Announcement;
